import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  Clock, MapPin, Sun, Sunrise, Moon, CloudSun, Sunset,
  Settings, Plus, Minus, ChevronLeft, ChevronRight, Search, X, Check, CheckCircle2, Navigation,
  Compass, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getPrayerTimes, getUserPrayerSettings, updatePrayerSettings, updatePrayer, getPrayersToday, logBereketActivity, getBereketActivities, getLocalDateString, parseLocalDateKey, shiftLocalDateKey } from '../lib/api';

const PRAYER_ICONS = {
  fajr: Sunrise,
  sunrise: Sun,
  dhuhr: CloudSun,
  asr: Sun,
  maghrib: Sunset,
  isha: Moon,
};

const PRAYER_NAMES = {
  en: {
    fajr: 'Fajr',
    sunrise: 'Sunrise',
    dhuhr: 'Dhuhr',
    asr: 'Asr',
    maghrib: 'Maghrib',
    isha: 'Isha',
  },
  hr: {
    fajr: 'Sabah',
    sunrise: 'Izlazak sunca',
    dhuhr: 'Podne',
    asr: 'Ikindija',
    maghrib: 'Akšam',
    isha: 'Jacija',
  },
};

const WORLD_CITIES = [
  { city: 'Zagreb', country: 'Croatia' },
  { city: 'Split', country: 'Croatia' },
  { city: 'Rijeka', country: 'Croatia' },
  { city: 'Sarajevo', country: 'Bosnia and Herzegovina' },
  { city: 'Mostar', country: 'Bosnia and Herzegovina' },
  { city: 'Banja Luka', country: 'Bosnia and Herzegovina' },
  { city: 'Tuzla', country: 'Bosnia and Herzegovina' },
  { city: 'Ljubljana', country: 'Slovenia' },
  { city: 'Beograd', country: 'Serbia' },
  { city: 'Novi Sad', country: 'Serbia' },
  { city: 'Nis', country: 'Serbia' },
  { city: 'Podgorica', country: 'Montenegro' },
  { city: 'Skopje', country: 'North Macedonia' },
  { city: 'Vienna', country: 'Austria' },
  { city: 'Munich', country: 'Germany' },
  { city: 'Berlin', country: 'Germany' },
  { city: 'Frankfurt', country: 'Germany' },
  { city: 'London', country: 'United Kingdom' },
  { city: 'Manchester', country: 'United Kingdom' },
  { city: 'Birmingham', country: 'United Kingdom' },
  { city: 'Paris', country: 'France' },
  { city: 'Marseille', country: 'France' },
  { city: 'Lyon', country: 'France' },
  { city: 'Istanbul', country: 'Turkey' },
  { city: 'Ankara', country: 'Turkey' },
  { city: 'Izmir', country: 'Turkey' },
  { city: 'Dubai', country: 'UAE' },
  { city: 'Abu Dhabi', country: 'UAE' },
  { city: 'Mecca', country: 'Saudi Arabia' },
  { city: 'Medina', country: 'Saudi Arabia' },
  { city: 'Riyadh', country: 'Saudi Arabia' },
  { city: 'Jeddah', country: 'Saudi Arabia' },
  { city: 'Cairo', country: 'Egypt' },
  { city: 'New York', country: 'USA' },
  { city: 'Los Angeles', country: 'USA' },
  { city: 'Chicago', country: 'USA' },
  { city: 'Toronto', country: 'Canada' },
  { city: 'Montreal', country: 'Canada' },
  { city: 'Sydney', country: 'Australia' },
  { city: 'Melbourne', country: 'Australia' },
];

export default function PrayerTimes({ user }) {
  const { i18n } = useTranslation();
  const [city, setCity] = useState('Sarajevo');
  const [country, setCountry] = useState('Bosnia and Herzegovina');
  const [prayerData, setPrayerData] = useState(null);
  const [prayersStatus, setPrayersStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [updating, setUpdating] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Qibla Compass State
  const [showQibla, setShowQibla] = useState(false);
  const [qiblaDirection, setQiblaDirection] = useState(null);
  const [deviceHeading, setDeviceHeading] = useState(0);
  const [qiblaLocation, setQiblaLocation] = useState(null);
  const [qiblaLoading, setQiblaLoading] = useState(false);
  const [qiblaError, setQiblaError] = useState(null);
  const [compassPermission, setCompassPermission] = useState(false);
  const compassRef = useRef(null);

  const lang = i18n.language;
  const isToday = selectedDate === getLocalDateString();

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (isToday) {
      fetchPrayersStatus();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (settings) {
      setCity(settings.default_city || 'Sarajevo');
      setCountry(settings.default_country || 'Bosnia and Herzegovina');
    }
  }, [settings]);

  useEffect(() => {
    if (city && country) {
      fetchPrayerTimes();
    }
  }, [city, country, selectedDate]);

  const fetchSettings = async () => {
    try {
      const data = await getUserPrayerSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPrayersStatus = async () => {
    try {
      const data = await getPrayersToday();
      setPrayersStatus(data);
    } catch (error) {
      console.error('Error fetching prayer status:', error);
    }
  };

  const saveSettings = async (updates) => {
    try {
      const data = await updatePrayerSettings(updates);
      setSettings(data);
      toast.success(lang === 'hr' ? 'Postavke spremljene' : 'Settings saved');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(lang === 'hr' ? 'Greška pri spremanju' : 'Error saving settings');
    }
  };

  const fetchPrayerTimes = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateObj = parseLocalDateKey(selectedDate);
      const day = dateObj.getDate();
      const month = dateObj.getMonth() + 1;
      const year = dateObj.getFullYear();

      const data = await getPrayerTimes(city, country, day, month, year);
      setPrayerData(data);
    } catch (err) {
      console.error('Error fetching prayer times:', err);
      setError(lang === 'hr' ? 'Greška pri dohvaćanju vremena namaza' : 'Error fetching prayer times');
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction) => {
    setSelectedDate(shiftLocalDateKey(selectedDate, direction));
  };

  const goToToday = () => {
    setSelectedDate(getLocalDateString());
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseLocalDateKey(dateStr);
    return date.toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const selectLocation = (selectedCity, selectedCountry) => {
    setCity(selectedCity);
    setCountry(selectedCountry);
    setShowLocationPicker(false);
    setLocationSearch('');
    saveSettings({ default_city: selectedCity, default_country: selectedCountry });
    toast.success(lang === 'hr' ? `Lokacija: ${selectedCity}` : `Location: ${selectedCity}`);
  };

  const filteredCities = WORLD_CITIES.filter(c =>
    c.city.toLowerCase().includes(locationSearch.toLowerCase()) ||
    c.country.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const getMyLocation = async () => {
    if (!navigator.geolocation) {
      toast.error(lang === 'hr' ? 'Geolokacija nije podržana' : 'Geolocation not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;

          const response = await fetch(
            `https://api.aladhan.com/v1/timings?latitude=${latitude}&longitude=${longitude}&method=3`
          );
          const result = await response.json();

          if (result.code === 200 && result.data) {
            const locationCity = result.data.meta.timezone.split('/').pop().replace(/_/g, ' ');
            const locationCountry = 'Current Location';

            setCity(locationCity);
            setCountry(locationCountry);
            setShowLocationPicker(false);
            saveSettings({ default_city: locationCity, default_country: locationCountry });
            toast.success(lang === 'hr' ? `Lokacija: ${locationCity}` : `Location: ${locationCity}`);
          }
        } catch (error) {
          console.error('Error getting location:', error);
          toast.error(lang === 'hr' ? 'Greška pri dohvaćanju lokacije' : 'Error getting location');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error(lang === 'hr' ? 'Niste dozvolili pristup lokaciji' : 'Location access denied');
        setGettingLocation(false);
      }
    );
  };

  const adjustTime = (timeStr, minutes) => {
    if (!timeStr) return '--:--';
    const [hours, mins] = timeStr.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMins = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMins < 0 ? 60 + newMins : newMins).padStart(2, '0')}`;
  };

  const getCurrentPrayer = () => {
    if (!prayerData?.timings) return null;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const prayers = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

    for (let i = prayers.length - 1; i >= 0; i--) {
      const timeStr = prayerData.timings[prayers[i]];
      if (timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const prayerTime = hours * 60 + minutes;
        if (currentTime >= prayerTime) {
          return prayers[i];
        }
      }
    }
    return 'isha';
  };

  const togglePrayer = async (prayerName) => {
    if (prayerName === 'sunrise') return;
    setUpdating(prayerName);
    try {
      const wasCompleted = prayersStatus?.[prayerName];
      await updatePrayer(prayerName, !wasCompleted);
      await fetchPrayersStatus();

      if (!wasCompleted) {
        const activities = await getBereketActivities();
        const prayerActivityName = `Pray ${prayerName.charAt(0).toUpperCase() + prayerName.slice(1)}`;
        const activity = activities.find(a => a.name_en === prayerActivityName);

        if (activity) {
          try {
            const displayName = lang === 'hr' ? activity.name_hr : activity.name_en;
            await logBereketActivity(activity.activity_id, activity.base_points, false, displayName);
            toast.success(`${lang === 'hr' ? 'Namaz obavljen! +' : 'Prayer completed! +'}${activity.base_points} ${lang === 'hr' ? 'Bereket bodova' : 'Bereket points'}`);
          } catch (bereketError) {
            console.log('Bereket logging info:', bereketError.message);
            toast.success(lang === 'hr' ? 'Namaz obavljen!' : 'Prayer completed!');
          }
        } else {
          toast.success(lang === 'hr' ? 'Namaz obavljen!' : 'Prayer completed!');
        }
      }
    } catch (error) {
      console.error('Error updating prayer:', error);
      toast.error(lang === 'hr' ? 'Greška pri ažuriranju' : 'Error updating prayer');
    } finally {
      setUpdating(null);
    }
  };

  // ==================== QIBLA COMPASS FUNCTIONS ====================
  
  const fetchQiblaDirection = async (lat, lon) => {
    setQiblaLoading(true);
    setQiblaError(null);
    try {
      const response = await fetch(`https://api.aladhan.com/v1/qibla/${lat}/${lon}`);
      const result = await response.json();
      
      if (result.code === 200) {
        setQiblaDirection(result.data.direction);
      } else {
        throw new Error('Failed to get Qibla direction');
      }
    } catch (err) {
      console.error('Error fetching Qibla:', err);
      setQiblaError(lang === 'hr' 
        ? 'Greška pri dohvaćanju smjera Kible' 
        : 'Error fetching Qibla direction');
    } finally {
      setQiblaLoading(false);
    }
  };

  const getQiblaLocation = () => {
    setQiblaLoading(true);
    setQiblaError(null);
    
    if (!navigator.geolocation) {
      setQiblaError(lang === 'hr' 
        ? 'Geolokacija nije podržana' 
        : 'Geolocation not supported');
      setQiblaLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setQiblaLocation({ latitude, longitude });
        fetchQiblaDirection(latitude, longitude);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setQiblaError(lang === 'hr' 
          ? 'Molimo omogućite pristup lokaciji' 
          : 'Please enable location access');
        setQiblaLoading(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const initCompass = async () => {
    if (!window.DeviceOrientationEvent) {
      setQiblaError(lang === 'hr' 
        ? 'Kompas nije podržan na ovom uređaju' 
        : 'Compass not supported on this device');
      return;
    }

    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          setCompassPermission(true);
          startCompass();
        } else {
          setQiblaError(lang === 'hr' 
            ? 'Pristup kompasu odbijen' 
            : 'Compass access denied');
        }
      } catch (err) {
        console.error('Error requesting orientation permission:', err);
        setQiblaError(lang === 'hr' 
          ? 'Greška pri pristupanju kompasu' 
          : 'Error accessing compass');
      }
    } else {
      setCompassPermission(true);
      startCompass();
    }
  };

  const startCompass = () => {
    window.addEventListener('deviceorientationabsolute', handleOrientation, true);
    window.addEventListener('deviceorientation', handleOrientation, true);
  };

  const handleOrientation = (event) => {
    let heading = event.alpha;
    
    if (event.webkitCompassHeading !== undefined) {
      heading = event.webkitCompassHeading;
    } else if (event.alpha !== null) {
      heading = event.absolute ? event.alpha : 360 - event.alpha;
    }
    
    if (heading !== null) {
      setDeviceHeading(Math.round(heading));
    }
  };

  const getCompassRotation = () => {
    if (qiblaDirection === null) return 0;
    return qiblaDirection - deviceHeading;
  };

  const isPointingToQibla = () => {
    if (qiblaDirection === null) return false;
    const diff = Math.abs(getCompassRotation());
    const normalizedDiff = diff > 180 ? 360 - diff : diff;
    return normalizedDiff <= 10;
  };

  const openQiblaCompass = () => {
    setShowQibla(true);
    getQiblaLocation();
    initCompass();
  };

  const closeQiblaCompass = () => {
    setShowQibla(false);
    window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
    window.removeEventListener('deviceorientation', handleOrientation, true);
  };

  // Cleanup compass listeners
  useEffect(() => {
    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, []);

  const currentPrayer = getCurrentPrayer();
  const prayerOrder = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
              {lang === 'hr' ? 'Vaktija' : 'Prayer Times'}
            </h1>
            <button
              onClick={() => setShowLocationPicker(true)}
              data-testid="prayer-times-change-location-button"
              className="flex items-center gap-2 text-[#57534E] hover:text-primary transition-colors"
            >
              <MapPin className="w-4 h-4" />
              <span className="font-medium">{city}, {country}</span>
              <span className="text-xs text-primary">({lang === 'hr' ? 'promijeni' : 'change'})</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openQiblaCompass}
              data-testid="qibla-compass-btn"
              className="flex items-center gap-2 px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all"
            >
              <Compass className="w-5 h-5" />
              <span className="hidden sm:inline">{lang === 'hr' ? 'Kibla' : 'Qibla'}</span>
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              data-testid="prayer-times-settings-toggle-button"
              className="p-3 bg-white border border-stone-200 rounded-xl hover:bg-stone-50 transition-all"
            >
              <Settings className="w-5 h-5 text-[#57534E]" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-4 mb-6">
          <button
            onClick={() => navigateDate(-1)}
            data-testid="prayer-times-previous-date-button"
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[#57534E]" />
          </button>

          <div className="text-center flex-1">
            <p className="text-lg font-semibold text-[#292524]">{formatDisplayDate(selectedDate)}</p>
            <div className="flex items-center justify-center gap-2 mt-1">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="prayer-times-date-input"
                className="text-sm text-primary bg-transparent cursor-pointer border-none"
              />
              {!isToday && (
                <button
                  onClick={goToToday}
                  data-testid="prayer-times-today-button"
                  className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                >
                  {lang === 'hr' ? 'Danas' : 'Today'}
                </button>
              )}
            </div>
          </div>

          <button
            onClick={() => navigateDate(1)}
            data-testid="prayer-times-next-date-button"
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-[#57534E]" />
          </button>
        </div>

        {showSettings && (
          <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card mb-6">
            <h2 className="text-lg font-heading font-semibold text-[#292524] mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              {lang === 'hr' ? 'Korekcije vremena' : 'Time Adjustments'}
            </h2>

            <div>
              <label className="block text-sm font-medium text-[#292524] mb-3">
                {lang === 'hr' ? 'Korekcija vremena (minuta)' : 'Time Adjustments (minutes)'}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].map(prayer => {
                  const adjustmentKey = `${prayer}_adjustment`;
                  return (
                    <div key={prayer} className="bg-stone-50 rounded-xl p-3">
                      <p className="text-sm font-medium text-[#292524] mb-2 text-center">
                        {PRAYER_NAMES[lang]?.[prayer] || prayer}
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => saveSettings({ [adjustmentKey]: (settings?.[adjustmentKey] || 0) - 1 })}
                          data-testid={`prayer-times-adjust-${prayer}-decrease-button`}
                          className="w-8 h-8 bg-white border border-stone-200 rounded-full flex items-center justify-center hover:bg-stone-100 transition-all"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center font-semibold text-[#292524]">
                          {settings?.[adjustmentKey] || 0}
                        </span>
                        <button
                          onClick={() => saveSettings({ [adjustmentKey]: (settings?.[adjustmentKey] || 0) + 1 })}
                          data-testid={`prayer-times-adjust-${prayer}-increase-button`}
                          className="w-8 h-8 bg-white border border-stone-200 rounded-full flex items-center justify-center hover:bg-stone-100 transition-all"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : prayerData ? (
          <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
            <div className="text-center mb-6 pb-6 border-b border-stone-200">
              <h2 className="text-2xl font-heading font-bold text-[#292524] mb-1">
                {prayerData.city || city}
              </h2>
              <p className="text-[#57534E]">{prayerData.date}</p>
              {prayerData.hijri_date && (
                <p className="text-sm text-primary mt-1">{prayerData.hijri_date}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {prayerOrder.map((prayer) => {
                const Icon = PRAYER_ICONS[prayer] || Clock;
                const isCurrentPrayer = prayer === currentPrayer;
                const isSunrise = prayer === 'sunrise';
                const isCompleted = prayersStatus?.[prayer];
                const rawTime = prayerData.timings?.[prayer];
                const adjustmentKey = `${prayer}_adjustment`;
                const adjustment = settings?.[adjustmentKey] || 0;
                const displayTime = isSunrise ? rawTime : adjustTime(rawTime, adjustment);

                return (
                  <div
                    key={prayer}
                    className={`relative p-4 rounded-xl transition-all border ${
                      isCurrentPrayer
                        ? 'bg-primary text-white shadow-lg border-primary scale-[1.02]'
                        : isCompleted
                        ? 'bg-green-50 border-green-200'
                        : isSunrise
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-stone-50 border-stone-100 hover:bg-stone-100'
                    }`}
                  >
                    {isCurrentPrayer && (
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-accent rounded-full animate-pulse"></div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          isCurrentPrayer ? 'bg-white/20' :
                          isCompleted ? 'bg-green-100' :
                          isSunrise ? 'bg-amber-100' : 'bg-primary/10'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            isCurrentPrayer ? 'text-white' :
                            isCompleted ? 'text-green-600' :
                            isSunrise ? 'text-amber-600' : 'text-primary'
                          }`} />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${
                            isCurrentPrayer ? 'text-white/80' :
                            isCompleted ? 'text-green-700' :
                            isSunrise ? 'text-amber-700' : 'text-[#57534E]'
                          }`}>
                            {PRAYER_NAMES[lang]?.[prayer] || prayer}
                          </p>
                          <p className={`text-xl font-bold ${
                            isCurrentPrayer ? 'text-white' :
                            isCompleted ? 'text-green-800' :
                            isSunrise ? 'text-amber-800' : 'text-[#292524]'
                          }`}>
                            {displayTime}
                          </p>
                        </div>
                      </div>

                      {!isSunrise && isToday && (
                        <button
                          onClick={() => togglePrayer(prayer)}
                          disabled={updating === prayer}
                          data-testid={`prayer-times-toggle-${prayer}-button`}
                          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                            isCurrentPrayer
                                ? 'bg-white/20 hover:bg-white/30 text-white'
                                : isCompleted
                                ? 'bg-green-100 hover:bg-green-200 text-green-600'
                                : 'bg-white border border-stone-200 hover:bg-stone-50 text-stone-400'
                          } ${updating === prayer ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {updating === prayer ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                          ) : isCompleted ? (
                            <CheckCircle2 className="w-6 h-6" />
                          ) : (
                            <Check className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-sm text-[#57534E] mt-6 pt-6 border-t border-stone-200">
              {lang === 'hr'
                ? 'Vremena su izračunata prema Islamic Society of North America (ISNA) metodi'
                : 'Times calculated using Islamic Society of North America (ISNA) method'}
            </p>
          </div>
        ) : null}
      </div>

      {showLocationPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLocationPicker(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-heading font-bold text-[#292524]">
                  {lang === 'hr' ? 'Odaberi Lokaciju' : 'Select Location'}
                </h2>
                <button onClick={() => setShowLocationPicker(false)} data-testid="prayer-times-location-picker-close-button" className="p-2 hover:bg-stone-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  data-testid="prayer-times-location-search-input"
                  placeholder={lang === 'hr' ? 'Pretraži grad...' : 'Search city...'}
                  className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-lg"
                />
              </div>
              <button
                onClick={getMyLocation}
                disabled={gettingLocation}
                data-testid="prayer-times-use-my-location-button"
                className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                {gettingLocation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>{lang === 'hr' ? 'Dohvaćanje...' : 'Getting location...'}</span>
                  </>
                ) : (
                  <>
                    <Navigation className="w-5 h-5" />
                    <span>{lang === 'hr' ? 'Koristi moju lokaciju' : 'Use My Location'}</span>
                  </>
                )}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {filteredCities.map((c, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectLocation(c.city, c.country)}
                    data-testid={`prayer-times-location-option-${idx}`}
                    className="w-full text-left p-4 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors"
                  >
                    <p className="font-medium text-[#292524]">{c.city}</p>
                    <p className="text-sm text-[#57534E]">{c.country}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Qibla Compass Modal */}
      {showQibla && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeQiblaCompass}>
          <div 
            className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Compass className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-heading font-bold text-[#292524]">
                    {lang === 'hr' ? 'Kibla Kompas' : 'Qibla Compass'}
                  </h2>
                </div>
                <button onClick={closeQiblaCompass} data-testid="prayer-times-qibla-close-button" className="p-2 hover:bg-stone-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Error Message */}
              {qiblaError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{qiblaError}</p>
                </div>
              )}

              {/* Loading State */}
              {qiblaLoading && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-3" />
                  <p className="text-[#57534E]">
                    {lang === 'hr' ? 'Učitavanje...' : 'Loading...'}
                  </p>
                </div>
              )}

              {/* Compass Display */}
              {!qiblaLoading && qiblaDirection !== null && (
                <div className="flex flex-col items-center">
                  {/* Compass Circle */}
                  <div 
                    className={`relative w-64 h-64 rounded-full border-4 transition-colors duration-300 ${
                      isPointingToQibla() 
                        ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200' 
                        : 'border-stone-300 bg-white'
                    }`}
                  >
                    {/* Cardinal Directions */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative w-full h-full">
                        <span className="absolute top-2 left-1/2 -translate-x-1/2 text-lg font-bold text-[#292524]">N</span>
                        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-lg font-bold text-[#57534E]">S</span>
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-lg font-bold text-[#57534E]">W</span>
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-bold text-[#57534E]">E</span>
                      </div>
                    </div>

                    {/* Compass Rose */}
                    <div className="absolute inset-4 rounded-full border border-stone-200">
                      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
                        <div
                          key={deg}
                          className="absolute w-1 h-3 bg-stone-300"
                          style={{
                            left: '50%',
                            top: '0',
                            transformOrigin: '50% calc(50% + ((100% - 6px) / 2))',
                            transform: `translateX(-50%) rotate(${deg}deg)`
                          }}
                        />
                      ))}
                    </div>

                    {/* Qibla Direction Needle */}
                    <div 
                      ref={compassRef}
                      className="absolute inset-0 flex items-center justify-center transition-transform duration-200 ease-out"
                      style={{ transform: `rotate(${getCompassRotation()}deg)` }}
                    >
                      <div className="relative h-full flex flex-col items-center">
                        {/* Kaaba Icon */}
                        <div className="absolute top-5">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                            isPointingToQibla() ? 'bg-green-500 text-white' : 'bg-primary text-white'
                          }`}>
                            <span className="text-lg">🕋</span>
                          </div>
                        </div>
                        
                        {/* Needle line */}
                        <div className="absolute top-16 w-1 bg-gradient-to-b from-primary to-transparent" style={{ height: 'calc(50% - 32px)' }} />
                      </div>
                    </div>

                    {/* Center dot */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-lg" />
                  </div>

                  {/* Status Message */}
                  <div className={`mt-4 px-5 py-2 rounded-xl flex items-center gap-2 transition-colors ${
                    isPointingToQibla() 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-stone-100 text-[#57534E]'
                  }`}>
                    {isPointingToQibla() ? (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-medium">
                          {lang === 'hr' ? 'Usmjereni ste prema Kibli!' : 'You are facing the Qibla!'}
                        </span>
                      </>
                    ) : (
                      <>
                        <Navigation className="w-5 h-5" />
                        <span className="text-sm">
                          {lang === 'hr' ? 'Okrećite se dok ne pronađete Kiblu' : 'Rotate until you find the Qibla'}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Qibla Info */}
                  <div className="mt-4 w-full grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-stone-50 rounded-xl">
                      <p className="text-xs text-[#57534E] mb-1">
                        {lang === 'hr' ? 'Smjer Kible' : 'Qibla Direction'}
                      </p>
                      <p className="text-xl font-bold text-primary">
                        {qiblaDirection?.toFixed(1)}°
                      </p>
                    </div>
                    <div className="text-center p-3 bg-stone-50 rounded-xl">
                      <p className="text-xs text-[#57534E] mb-1">
                        {lang === 'hr' ? 'Vaš smjer' : 'Your Heading'}
                      </p>
                      <p className="text-xl font-bold text-[#292524]">
                        {deviceHeading}°
                      </p>
                    </div>
                  </div>

                  {qiblaLocation && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[#57534E]">
                      <MapPin className="w-3 h-3" />
                      <span>
                        {qiblaLocation.latitude.toFixed(4)}°, {qiblaLocation.longitude.toFixed(4)}°
                      </span>
                    </div>
                  )}

                  {/* Instructions */}
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl w-full">
                    <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {lang === 'hr' ? 'Upute' : 'Instructions'}
                    </h3>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li>• {lang === 'hr' 
                        ? 'Držite telefon horizontalno' 
                        : 'Hold your phone horizontally'}</li>
                      <li>• {lang === 'hr' 
                        ? 'Udalite se od metalnih predmeta' 
                        : 'Move away from metal objects'}</li>
                      <li>• {lang === 'hr' 
                        ? 'Kalibrirajte kompas kružnim pokretima' 
                        : 'Calibrate with figure-8 motion'}</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Desktop fallback - when no compass */}
              {!qiblaLoading && qiblaDirection !== null && !compassPermission && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-center">
                  <p className="text-blue-700 text-sm">
                    {lang === 'hr' 
                      ? 'Za kompas, koristite mobilni uređaj.' 
                      : 'For compass, use a mobile device.'}
                  </p>
                  <p className="text-blue-600 mt-2 font-medium">
                    {lang === 'hr' 
                      ? `Smjer Kible: ${qiblaDirection?.toFixed(1)}° od sjevera` 
                      : `Qibla direction: ${qiblaDirection?.toFixed(1)}° from North`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
